import type { AnimationItem } from 'lottie-web';
import { useMotionValueEvent, useReducedMotion, useScroll } from 'motion/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { SECTIONS, SECTION_BLURB } from 'src/data/sections.ts';
import { shortSectionTitle } from 'src/lib/scoring.ts';
import { Section } from 'src/types/types.ts';

// Animiertes Framework (zeb-Handoff, Lottie): Pyramide, deren drei Ebenen
// nacheinander in den Fokus rücken. Timeline (~8,4 s / 251 Frames):
//   Ebene 1 – Strategie: Pyramide zusammengesetzt, Spitze betont (~0–2,8 s)
//   Ebene 2 – Loop:      mittlere Schicht klappt in vier Teile auf + rotiert (~2,8–6,6 s)
//   Ebene 3 – Wirkung:   Basis klappt auf (~6,6–8,4 s)
const LOTTIE_SRC = '/framework/new/pyramid.json';
const POSTER_SRC = '/framework/new/pyramid-poster.png';

// Das Lottie ist 1920×1080 mit viel Weißraum um die Pyramide. Per CSS-Crop auf
// den Pyramiden-Ausschnitt zoomen (Werte identisch zum früheren MP4-Crop),
// damit die Box-Maße und damit die Blockanordnung gleich bleiben.
const L_W = 1920;
const L_H = 1080;
const CROP = { x: 660, y: 245, w: 600, h: 680 };

// Anzeigebreite der gecroppten Pyramidenbox (Desktop). Höhe folgt aus CROP.
// Bewusst groß gewählt: die beiden rechtsseitigen Blöcke (01 Spitze, 03 Basis)
// brauchen genug vertikalen Abstand, damit ihre langen Blurbs nicht kollidieren.
const VIEW_W = 420;
const VIEW_H = Math.round((VIEW_W * CROP.h) / CROP.w);
const BLOCK_W = 270;
// Abstand von der inneren Blockkante bis zur Pyramiden-Mittelachse (Verbindungspunkt).
const CONNECTOR_W = 182;
const HEADING_HALF = 14; // halbe h3-Zeilenhöhe → richtet die Linie am Heading aus

// Desktop-Anker je SECTIONS-Index: tier = vertikale Position auf der Box, side = Blockseite.
// Strategie (Spitze) + Wirkungsebenen (Basis) links, Loop (Mitte) rechts.
const ANCHORS = [
  { tier: 0.22, side: 'left' as const },
  { tier: 0.46, side: 'right' as const },
  { tier: 0.72, side: 'left' as const }
];

// Scroll-Fortschritt (0–1) → aktive Ebene, an der Video-Timeline ausgerichtet.
// b1: Ebene 1 (Strategie) nur solange die Pyramide vollständig zusammengesetzt ist
// (bis ~p 0.26); sobald Ebene 2 aufzuklappen beginnt, ist Loop aktiv.
// b2: Ebene 3 (Wirkung) aktiv, sobald die Basis aufzuklappen beginnt.
const PHASE_BOUNDS = [0.26, 0.79] as const;
function activeFromProgress(p: number) {
  if (p < PHASE_BOUNDS[0]) return 0;
  if (p < PHASE_BOUNDS[1]) return 1;
  return 2;
}

// null = keine aktive Ebene (statische Variante) → alle Blöcke voll sichtbar.
const dim = (active: number | null, i: number) => (active === null || active === i ? 1 : 0.4);

// ---------------------------------------------------------------------------
// Lottie-Ressourcen: beides modulweit gecacht, damit ein Breakpoint-Wechsel
// (Scroll ↔ Autoplay remountet die Komponente) weder den Player noch das
// ~550-KB-JSON erneut lädt/parst. Der Player kommt als dynamischer Import
// (lottie_light, nur SVG-Renderer) und bleibt so aus dem Haupt-Bundle.
// ---------------------------------------------------------------------------
let lottiePromise: Promise<(typeof import('lottie-web'))['default']> | null = null;
function loadLottie() {
  lottiePromise ??= import('lottie-web/build/player/lottie_light').then((m) => m.default);
  return lottiePromise;
}

let pyramidDataPromise: Promise<Record<string, unknown>> | null = null;
function loadPyramidData() {
  // Fehler nicht cachen, damit ein späterer Mount erneut versuchen kann.
  pyramidDataPromise ??= fetch(LOTTIE_SRC)
    .then((res) => {
      if (!res.ok) throw new Error(`Lottie-Fetch fehlgeschlagen: ${res.status}`);
      return res.json() as Promise<Record<string, unknown>>;
    })
    .catch((err) => {
      pyramidDataPromise = null;
      throw err;
    });
  return pyramidDataPromise;
}

/** Statisches Pyramiden-Poster in derselben Box wie das gecroppte Lottie
 * (Poster-Datei ist exakt der CROP-Ausschnitt, 600×680). */
function PosterImage({ viewW }: { viewW: number }) {
  return (
    <img
      src={POSTER_SRC}
      alt=""
      aria-hidden="true"
      width={CROP.w}
      height={CROP.h}
      className="pointer-events-none h-auto"
      style={{ width: viewW }}
    />
  );
}

/**
 * Lottie-Pyramide, per CSS auf den Pyramiden-Ausschnitt gecroppt.
 * mode 'scrub'  → Frame wird von außen (Scroll) gesetzt; `animRef` liefert die Instanz.
 * mode 'autoplay' → läuft als Loop (Mobile), offscreen pausiert.
 *
 * Geladen wird erst, wenn die Sektion in Viewport-Nähe kommt (Poster als
 * Platzhalter); schlägt der Fetch fehl, bleibt das Poster stehen.
 */
function CroppedLottie({
  viewW,
  mode,
  animRef,
  onReady
}: {
  viewW: number;
  mode: 'scrub' | 'autoplay';
  animRef?: React.MutableRefObject<AnimationItem | null>;
  onReady?: (anim: AnimationItem) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [ready, setReady] = useState(false);
  // onReady im Ref halten, damit der Lade-Effekt nicht an der Callback-Identität hängt.
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  });

  // Download/Parse erst kurz bevor die Sektion sichtbar wird — das JSON gehört
  // nicht auf den kritischen Pfad der Landingpage.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNearViewport(true);
          io.disconnect();
        }
      },
      { rootMargin: '800px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!nearViewport || !hostRef.current) return;
    let cancelled = false;
    let anim: AnimationItem | null = null;
    let pauseObserver: IntersectionObserver | null = null;

    Promise.all([loadLottie(), loadPyramidData()])
      .then(([lottie, animationData]) => {
        if (cancelled || !hostRef.current) return;
        anim = lottie.loadAnimation({
          container: hostRef.current,
          renderer: 'svg',
          loop: mode === 'autoplay',
          autoplay: mode === 'autoplay',
          // Kopie pro Instanz: lottie-web mutiert die übergebenen Daten.
          animationData: structuredClone(animationData),
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
        });
        if (animRef) animRef.current = anim;
        setReady(true);
        // Mit animationData ist die Instanz sofort vollständig (totalFrames
        // gesetzt) → Parent kann den Frame direkt zur Scroll-Position syncen.
        onReadyRef.current?.(anim);
        // Autoplay offscreen pausieren: der rAF-Loop von lottie-web liefe sonst
        // für die gesamte Seiten-Lebensdauer weiter (Main-Thread + Akku).
        if (mode === 'autoplay' && boxRef.current) {
          pauseObserver = new IntersectionObserver(([entry]) => {
            if (!anim) return;
            if (entry.isIntersecting) anim.play();
            else anim.pause();
          });
          pauseObserver.observe(boxRef.current);
        }
      })
      .catch(() => {
        // Fetch/Parse fehlgeschlagen → Poster bleibt als Fallback stehen.
      });

    return () => {
      cancelled = true;
      pauseObserver?.disconnect();
      anim?.destroy();
      if (animRef) animRef.current = null;
    };
  }, [nearViewport, mode, animRef]);

  const scale = viewW / CROP.w;
  return (
    <div
      ref={boxRef}
      className="pointer-events-none relative overflow-hidden"
      style={{ width: viewW, height: CROP.h * scale }}
      aria-hidden="true">
      <div
        ref={hostRef}
        style={{
          position: 'absolute',
          left: -CROP.x * scale,
          top: -CROP.y * scale,
          width: L_W * scale,
          height: L_H * scale
        }}
      />
      {!ready && (
        <img src={POSTER_SRC} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
    </div>
  );
}

/** Verbindungslinie + Punkt, am Heading ausgerichtet, bis zur Pyramiden-Mittelachse. */
function Connector({ goLeft }: { goLeft: boolean }) {
  return (
    <div
      className="pointer-events-none absolute -translate-y-1/2"
      style={{
        top: HEADING_HALF,
        width: CONNECTOR_W,
        height: 2,
        [goLeft ? 'right' : 'left']: '100%'
      }}>
      <div className="h-full w-full bg-zeb-primary" />
      <div
        className="absolute top-1/2 h-3 w-3 rounded-full bg-zeb-primary ring-4 ring-zeb-primary/20"
        style={
          goLeft
            ? { left: 0, transform: 'translate(-50%, -50%)' }
            : { right: 0, transform: 'translate(50%, -50%)' }
        }
      />
    </div>
  );
}

/** Heading (Nummer + Kurztitel) + Blurb einer Ebene. Opacity setzt der Parent. */
function TextBlock({ section, index }: { section: Section; index: number }) {
  return (
    <div>
      <h3
        className="text-xl tracking-tight text-zeb-tertiary"
        style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, letterSpacing: '-0.02em' }}>
        <span className="text-zeb-primary">0{index + 1}</span> {shortSectionTitle(section)}
      </h3>
      <p
        className="mt-2 text-body-sm leading-relaxed text-zeb-gray-600"
        style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
        {SECTION_BLURB[section.id]}
      </p>
    </div>
  );
}

/** Desktop-Bühne: Pyramiden-Media zentriert, Blöcke flankierend mit
 * Verbindungslinien. Wird vom Scroll-Scrubbing (activeIndex 0–2) und von der
 * statischen Reduced-Motion-Variante (activeIndex null) geteilt. */
function DesktopStage({ media, activeIndex }: { media: ReactNode; activeIndex: number | null }) {
  return (
    <div className="relative mx-auto w-full" style={{ height: VIEW_H }}>
      <div className="absolute left-1/2 top-0 -translate-x-1/2">{media}</div>
      {SECTIONS.map((s, i) => {
        const a = ANCHORS[i];
        const goLeft = a.side === 'right';
        const headingTop = Math.round(a.tier * VIEW_H) - HEADING_HALF;
        const pos =
          a.side === 'right'
            ? { left: `calc(50% + ${CONNECTOR_W}px)` }
            : { right: `calc(50% + ${CONNECTOR_W}px)` };
        return (
          <div
            key={s.id}
            className="absolute transition-opacity duration-300"
            style={{ width: BLOCK_W, top: headingTop, opacity: dim(activeIndex, i), ...pos }}>
            <div className={goLeft ? 'pl-5' : ''}>
              <TextBlock section={s} index={i} />
            </div>
            <Connector goLeft={goLeft} />
          </div>
        );
      })}
    </div>
  );
}

/** Desktop (lg+): Pyramide zentriert, Blöcke flankierend mit Verbindungslinien,
 * das Lottie wird per Scroll gescrubbt. */
function ScrollFramework() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start start', 'end end']
  });
  const [activeIndex, setActiveIndex] = useState(0);

  // Frame + aktive Ebene aus dem Scroll-Fortschritt ableiten. Läuft auch im
  // onReady-Callback, damit die Pyramide nach dem asynchronen Laden sofort zur
  // aktuellen Scroll-Position passt (z. B. Reload mit Scroll-Restoration) —
  // nicht erst beim nächsten Scroll-Event.
  const applyProgress = (p: number) => {
    const anim = animRef.current;
    if (anim && anim.totalFrames) {
      const clamped = Math.max(0, Math.min(1, p));
      anim.goToAndStop(clamped * (anim.totalFrames - 1), true);
    }
    setActiveIndex(activeFromProgress(p));
  };
  useMotionValueEvent(scrollYProgress, 'change', applyProgress);

  return (
    <div ref={wrapperRef} className="relative mt-10 h-[300vh]">
      <div className="sticky top-0 flex h-screen items-center justify-center">
        <DesktopStage
          media={
            <CroppedLottie
              viewW={VIEW_W}
              mode="scrub"
              animRef={animRef}
              onReady={() => applyProgress(scrollYProgress.get())}
            />
          }
          activeIndex={activeIndex}
        />
      </div>
    </div>
  );
}

/** Gestapeltes Layout für Mobile: Pyramide oben, Blöcke darunter. */
function StackedStage({ media }: { media: ReactNode }) {
  return (
    <div className="mt-10 flex flex-col items-center gap-8">
      {media}
      <div className="w-full max-w-sm space-y-5">
        {SECTIONS.map((s, i) => (
          <div key={s.id} className="border-l-2 border-zeb-primary pl-4">
            <TextBlock section={s} index={i} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mobile: kein Scroll-Pinning – das Lottie spielt als Loop automatisch ab. */
function AutoplayFramework() {
  return <StackedStage media={<CroppedLottie viewW={280} mode="autoplay" />} />;
}

/** Reduced-Motion: statisches Poster (kein Lottie-Download). Desktop behält
 * das flankierende Layout mit Verbindungslinien — nur ohne Pinning/Animation;
 * unterhalb von lg wird gestapelt. */
function StaticFramework({ isDesktop }: { isDesktop: boolean }) {
  if (isDesktop) {
    return (
      <div className="mt-10">
        <DesktopStage media={<PosterImage viewW={VIEW_W} />} activeIndex={null} />
      </div>
    );
  }
  return <StackedStage media={<PosterImage viewW={280} />} />;
}

/** Tailwind `lg` – entscheidet über Scroll-Scrubbing vs. Autoplay.
 * Initialwert direkt aus matchMedia (Client-only SPA), damit der erste Paint auf
 * Desktop nicht kurz das Mobile-Layout zeigt (Flackern + doppelter Lottie-Load). */
const DESKTOP_MQ = '(min-width: 1024px)';
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_MQ).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return isDesktop;
}

export function FrameworkScrollytelling() {
  const reduced = useReducedMotion();
  const isDesktop = useIsDesktop();

  if (reduced) return <StaticFramework isDesktop={isDesktop} />;
  if (!isDesktop) return <AutoplayFramework />;
  return <ScrollFramework />;
}
